import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Type } from '@angular/core';
import { catchError, map, Observable, of, ReplaySubject, switchMap, tap } from 'rxjs';
import { DynamicPageDcHeadingTextComponent } from '../../components/dynamic-components/dynamic-page-dc-heading-text/dynamic-page-dc-heading-text.component';
import { DynamicPageDcBaseComponent } from '../../components/dynamic-components/dynamic-page-dc-base/dynamic-page-dc-base.component';
import { DynamicPageDcGridComponent } from '../../components/dynamic-components/dynamic-page-dc-grid/dynamic-page-dc-grid.component';
import { DynamicPageDcInfoCardComponent } from '../../components/dynamic-components/dynamic-page-dc-info-card/dynamic-page-dc-info-card.component';
import { DynamicPageDcProductsListComponent } from '../../components/dynamic-components/dynamic-page-dc-products-list/dynamic-page-dc-products-list.component';
import { DynamicPageDcButtonComponent } from '../../components/dynamic-components/dynamic-page-dc-button/dynamic-page-dc-button.component';
import { DynamicPageDcImageComponent } from '../../components/dynamic-components/dynamic-page-dc-image/dynamic-page-dc-image.component';
import { DynamicPageDcImageAndTextComponent } from '../../components/dynamic-components/dynamic-page-dc-image-and-text/dynamic-page-dc-image-and-text.component';
import { DynamicPageDcImageCarouselComponent } from '../../components/dynamic-components/dynamic-page-dc-image-carousel/dynamic-page-dc-image-carousel.component';
import { DynamicPageDcProductListCarouselComponent } from '../../components/dynamic-components/dynamic-page-dc-product-list-carousel/dynamic-page-dc-product-list-carousel.component';
import { DynamicPageDcVideoComponent } from '../../components/dynamic-components/dynamic-page-dc-video/dynamic-page-dc-video.component';
import { DynamicPageDcContainerComponent } from '../../components/dynamic-components/dynamic-page-dc-container/dynamic-page-dc-container.component';

export interface DynamicPage {
  id: string | null;
  name: string;
  slug?: string;
  elements: DynamicPageElement[];
}

export interface DynamicPageElement {
  id: string;
  fields: { [s: string]: any }
}

export interface DynamicComponent {
  id: string,
  component: Type<DynamicPageDcBaseComponent>,
  name: string
}

@Injectable({
  providedIn: 'root'
})
export class DynamicPagesService {
  protected httpClient = inject(HttpClient)
  selectedPageViewer: DynamicPage | null = null;
  private baseUrl = 'json/dynamic-pages';
  private dynamicPages$: ReplaySubject<DynamicPage[]> | null = null;


  private internalGetDynamicPages(noCache = false): Observable<DynamicPage[]> {
    const headers = {
      maxAge: noCache ? '-1' : '300'
    };
    return this.httpClient.get<DynamicPage[]>(`${this.baseUrl}`, { headers });
  }

  private internalGetDynamicPageById(id: string, noCache = false): Observable<DynamicPage> {
    const headers = {
      maxAge: noCache ? '-1' : '300'
    };
    return this.httpClient.get<DynamicPage>(`${this.baseUrl}/${id}`, { headers });
  }

  getDynamicPages(noCache = false): Observable<DynamicPage[]> {
    if (!this.dynamicPages$) {
      this.dynamicPages$ = new ReplaySubject<DynamicPage[]>(1);
      const subject = this.dynamicPages$;
      return this.internalGetDynamicPages(noCache).pipe(
        tap(pages => subject.next(pages)),
        switchMap(() => subject.asObservable())
      );
    }
    return this.dynamicPages$.asObservable();
  }

  getDynamicPage(id: string, noCache = false): Observable<DynamicPage> {
    return this.internalGetDynamicPageById(id, noCache);
  }

  createDynamicPage(page: DynamicPage): Observable<DynamicPage> {
    return this.httpClient.post<DynamicPage>(`${this.baseUrl}`, page).pipe(
      tap(() => this.refreshList())
    );
  }

  updateDynamicPage(page: DynamicPage): Observable<DynamicPage> {
    return this.httpClient.put<DynamicPage>(`${this.baseUrl}/${page.id}`, page).pipe(
      tap(() => this.refreshList())
    );
  }

  deleteDynamicPage(id: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUrl}/${id}`).pipe(
      tap(() => this.refreshList())
    );
  }

  private refreshList(): void {
    if (this.dynamicPages$) {
      this.internalGetDynamicPages(true).subscribe(pages => this.dynamicPages$?.next(pages));
    }
  }

  pageElements(): DynamicComponent[] {
    return [
      {
        id: 'grid',
        component: DynamicPageDcGridComponent,
        name: 'Grid',
      },
      {
        id: 'container',
        component: DynamicPageDcContainerComponent,
        name: 'Container'
      },
      {
        id: 'heading-text',
        component: DynamicPageDcHeadingTextComponent,
        name: 'Heading and Text',
      },
      {
        id: 'info-card',
        component: DynamicPageDcInfoCardComponent,
        name: 'Card Info'
      },
      {
        id: 'product-list',
        component: DynamicPageDcProductsListComponent,
        name: 'Product list',
      },
      {
        id: 'product-list-carousel',
        component: DynamicPageDcProductListCarouselComponent,
        name: 'Product list carousel',
      },
      {
        id: 'button',
        component: DynamicPageDcButtonComponent,
        name: 'Button',
      },
      {
        id: 'image',
        component: DynamicPageDcImageComponent,
        name: 'Image'
      },
      {
        id: 'image-and-text',
        component: DynamicPageDcImageAndTextComponent,
        name: 'Image and text'
      },
      {
        id: 'image-carousel',
        component: DynamicPageDcImageCarouselComponent,
        name: 'Image Carousel'
      },
      {
        id: 'video',
        component: DynamicPageDcVideoComponent,
        name: 'Video'
      }
    ]
  }

  getComponentById(id: string): any | null {
    return this.pageElements().find(c => c.id === id)?.component ?? null;
  }

  getComponentNameById(id: string): any | null {
    return this.pageElements().find(c => c.id === id)?.name ?? null;
  }

  getDynamicPageBySlug(slug: string, noCache = false): Observable<DynamicPage> {
    const headers = { maxAge: noCache ? '-1' : '300' };
    return this.httpClient.get<DynamicPage>(`${this.baseUrl}/slug/${slug}`, { headers });
  }

  checkSlugEndpointActive(): Observable<boolean> {
    return this.httpClient.get(`${this.baseUrl}/slug/__check__`).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  setPageViewer(page: DynamicPage) {
    this.selectedPageViewer = page;
  }
}
